import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import {
    DEBUG_PARAMETRE_DOCUMENT_API,
    ParametreDocumentBundle,
    ParametreDocumentPutBody,
    ParametreDocumentSettingsService,
    ParametreDocumentStationSettings,
    VenteAchatParametreRow
} from './parametre-document-settings.service';
import { isTruthyTinyInt, normalizeBaseCalculPrixAchat, normalizeVenteAchatRow } from './parametre-doc-api-normalize';
import { getEnterpriseStationIdFromLocalStorage } from '../enterprise-context';
import { parseModelesReferenceMap } from './parametre-document-type-ids';

export function getStationIdFromLocalStorage(): number {
    const sid = getEnterpriseStationIdFromLocalStorage();
    if (sid === 0 && DEBUG_PARAMETRE_DOCUMENT_API) {
        console.warn('[ParametreDocument] station_id : utilisateur absent ou JSON invalide');
    }
    return sid;
}

@Injectable({
    providedIn: 'root'
})
export class ParametreDocumentStateService {
    parametreDocumentId: number | null = null;
    station: Partial<ParametreDocumentStationSettings> = {};
    venteAchat: VenteAchatParametreRow[] = [];

    private loaded = false;
    loading = false;

    constructor(
        private api: ParametreDocumentSettingsService,
        private toastr: ToastrService
    ) {}

    getStationId(): number {
        return getStationIdFromLocalStorage();
    }

    isParametreDocumentLoaded(): boolean {
        return this.loaded;
    }

    ensureLoaded(): Observable<void> {
        const sid = this.getStationId();
        if (sid === 0) {
            return of(undefined);
        }
        if (this.loaded) {
            return of(undefined);
        }
        this.loading = true;
        return this.api.getByStation(sid).pipe(
            tap((bundle) => {
                if (bundle != null) {
                    this.applyBundle(bundle);
                } else {
                    this.parametreDocumentId = null;
                    this.station = { station_id: sid };
                    this.venteAchat = [];
                }
                this.loaded = true;
            }),
            map(() => undefined),
            finalize(() => {
                this.loading = false;
            })
        );
    }

    reload(): Observable<void> {
        this.loaded = false;
        return this.ensureLoaded();
    }

    applyBundle(bundle: ParametreDocumentBundle): void {
        const pd = bundle.parametre_document ?? {};
        this.parametreDocumentId = pd.id != null ? Number(pd.id) : null;
        this.station = {
            ...pd,
            modeles_reference: parseModelesReferenceMap(pd.modeles_reference)
        };
        this.venteAchat = Array.isArray(bundle.vente_achat)
            ? bundle.vente_achat.map((r) => normalizeVenteAchatRow(r))
            : [];
    }

    getRowForType(typeDocumentId: number): VenteAchatParametreRow | undefined {
        const tid = Number(typeDocumentId);
        return this.venteAchat.find((r) => Number(r.type_document_id) === tid);
    }

    defaultRow(typeDocumentId: number, category: 'vente' | 'achat'): VenteAchatParametreRow {
        const sid = this.getStationId();
        return {
            station_id: sid,
            type_document_id: typeDocumentId,
            document_category: category,
            aff_montant_totaux: 1,
            aff_inf_contact: 1,
            aff_courte_desp: 1,
            aff_num_bn_liv: 1,
            note: null,
            label: null,
            couleur: null,
            date_paiement: 'jours',
            nb_date: 1,
            insr_orig_artcl: 0,
            insr_qr_cd: 0
        };
    }

    getRowOrDefault(typeDocumentId: number, category: 'vente' | 'achat'): VenteAchatParametreRow {
        const existing = this.getRowForType(typeDocumentId);
        return existing != null ? { ...existing } : this.defaultRow(typeDocumentId, category);
    }

    mergeVenteAchatRow(
        typeDocumentId: number,
        category: 'vente' | 'achat',
        patch: Partial<VenteAchatParametreRow>
    ): void {
        const sid = this.getStationId();
        const tid = Number(typeDocumentId);
        const idx = this.venteAchat.findIndex((r) => Number(r.type_document_id) === tid);
        const base =
            idx >= 0
                ? { ...this.venteAchat[idx] }
                : this.defaultRow(tid, category);
        const merged: VenteAchatParametreRow = {
            ...base,
            ...patch,
            type_document_id: tid,
            document_category: category,
            station_id: sid
        };
        if (idx >= 0) {
            this.venteAchat[idx] = merged;
        } else {
            this.venteAchat.push(merged);
        }
    }

    buildPutBody(): ParametreDocumentPutBody {
        const mr = this.station.modele_reference;
        let modele_reference: number | null = null;
        if (mr !== undefined && mr !== null) {
            const n = Number(mr);
            if (Number.isFinite(n)) {
                modele_reference = Math.floor(n);
            }
        }
        const modeles_reference = parseModelesReferenceMap(this.station.modeles_reference);
        const body: ParametreDocumentPutBody = {
            active_bon_liv: isTruthyTinyInt(this.station.active_bon_liv),
            modele_reference,
            modeles_reference: Object.keys(modeles_reference).length ? modeles_reference : null,
            maj_prix_achat_produit: isTruthyTinyInt(this.station.maj_prix_achat_produit),
            base_calcul_prix_achat: normalizeBaseCalculPrixAchat(this.station.base_calcul_prix_achat),
            aff_produit_matriciel: isTruthyTinyInt(
                this.station.aff_produit_matriciel !== undefined ? this.station.aff_produit_matriciel : 1
            ),
        };
        if (this.loaded) {
            body.vente_achat = this.venteAchat.map((r) => normalizeVenteAchatRow(r));
        }
        return body;
    }

    /**
     * PUT renvoie souvent une erreur pour une station sans ligne encore créée :
     * message du type « Utilisez POST pour créer » → on retente en POST.
     */
    private shouldRetrySaveWithPost(err: unknown): boolean {
        const e = err as { status?: number; error?: unknown };
        const status = e?.status;
        const body = e?.error;
        let msg = '';
        if (typeof body === 'string') {
            msg = body;
        } else if (body != null && typeof body === 'object') {
            const o = body as Record<string, unknown>;
            msg = String(o['message'] ?? o['error'] ?? '');
            if (!msg && typeof o['errors'] === 'object' && o['errors'] !== null) {
                const errs = o['errors'] as Record<string, unknown>;
                const parts: string[] = [];
                for (const val of Object.values(errs)) {
                    if (Array.isArray(val)) {
                        val.forEach((s) => typeof s === 'string' && parts.push(s));
                    } else if (typeof val === 'string') {
                        parts.push(val);
                    }
                }
                msg = parts.join(' ');
            }
        }
        const lower = msg.toLowerCase();
        const hintsPost =
            lower.includes('post') &&
            (lower.includes('créer') || lower.includes('creer') || lower.includes('create'));
        const hintsNone =
            lower.includes('aucun paramètre document') ||
            lower.includes('aucun parametre document') ||
            lower.includes('no parametre') ||
            hintsPost;
        if (!hintsNone && (status === 404 || status === 405)) {
            return true;
        }
        return hintsNone;
    }

    saveBundle(): Observable<unknown> {
        const sid = this.getStationId();
        if (sid === 0) {
            this.toastr.warning('Station introuvable.', 'Enregistrement');
            return of(null);
        }
        const body = this.buildPutBody();
        const req$ = this.api.update(sid, body).pipe(
            catchError((err) => {
                if (this.shouldRetrySaveWithPost(err)) {
                    return this.api.create(sid, body);
                }
                return throwError(() => err);
            })
        );
        return req$.pipe(
            tap((res: unknown) => {
                const data =
                    res != null && typeof res === 'object' && 'data' in res
                        ? (res as { data: unknown }).data
                        : res;
                if (data != null && typeof data === 'object') {
                    const d = data as Record<string, unknown>;
                    const abl =
                        d['active_bon_liv'] ??
                        (typeof d['parametre_document'] === 'object' && d['parametre_document'] != null
                            ? (d['parametre_document'] as Record<string, unknown>)['active_bon_liv']
                            : undefined);
                    if (abl !== undefined) {
                        this.station = { ...this.station, active_bon_liv: abl as boolean | 0 | 1 };
                    }
                    const mref =
                        d['modele_reference'] ??
                        (typeof d['parametre_document'] === 'object' && d['parametre_document'] != null
                            ? (d['parametre_document'] as Record<string, unknown>)['modele_reference']
                            : undefined);
                    if (mref !== undefined) {
                        const n = Number(mref);
                        this.station = {
                            ...this.station,
                            modele_reference: Number.isFinite(n) ? Math.floor(n) : null
                        };
                    }
                    const mrefs =
                        d['modeles_reference'] ??
                        (typeof d['parametre_document'] === 'object' && d['parametre_document'] != null
                            ? (d['parametre_document'] as Record<string, unknown>)['modeles_reference']
                            : undefined);
                    if (mrefs !== undefined) {
                        this.station = {
                            ...this.station,
                            modeles_reference: parseModelesReferenceMap(mrefs)
                        };
                    }
                    const pd = d['parametre_document'];
                    if (pd != null && typeof pd === 'object') {
                        this.station = { ...this.station, ...(pd as Partial<ParametreDocumentStationSettings>) };
                        const id = (pd as { id?: unknown }).id;
                        if (id != null) {
                            this.parametreDocumentId = Number(id);
                        }
                    }
                    if (Array.isArray(d['vente_achat'])) {
                        this.venteAchat = (d['vente_achat'] as VenteAchatParametreRow[]).map((r) =>
                            normalizeVenteAchatRow(r)
                        );
                    }
                }
            })
        );
    }

    saveWithVenteAchatPatch(
        typeDocumentId: number,
        category: 'vente' | 'achat',
        patch: Partial<VenteAchatParametreRow>
    ): Observable<unknown> {
        return this.ensureLoaded().pipe(
            switchMap(() => {
                this.mergeVenteAchatRow(typeDocumentId, category, patch);
                return this.saveBundle();
            })
        );
    }

    reset(): void {
        this.loaded = false;
        this.parametreDocumentId = null;
        this.station = {};
        this.venteAchat = [];
    }
}
